import { useState, useEffect, useCallback } from 'react';
import { api, getErrorMessage } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Target, Plus, Edit, Trash2, Settings, FolderPlus, 
  DollarSign, Percent, Users, Bed, ArrowLeft, RefreshCw,
  Building2, Hotel
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const CATEGORY_TYPES = [
  { value: 'fixed', label: 'Fixed Cost', description: 'Same amount every month (e.g., Rent, Salaries)' },
  { value: 'variable', label: 'Variable Cost', description: 'Changes based on occupancy (e.g., Housekeeping, Utilities)' }
];

const VARIABLE_COST_TYPES = [
  { value: 'per_room_night', label: 'Per Room Night', icon: Bed, description: 'Cost per occupied room per night' },
  { value: 'per_guest_night', label: 'Per Guest Night', icon: Users, description: 'Cost per guest per night' },
  { value: 'percentage_revenue', label: '% of Revenue', icon: Percent, description: 'Percentage of room revenue' }
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

export default function BudgetSetup({ user, selectedCompany }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('categories');
  const [loading, setLoading] = useState(true);
  
  // Data
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  
  // Dialog states
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  
  // Selected items
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    category_type: 'fixed',
    display_order: 0
  });
  
  const [itemForm, setItemForm] = useState({
    category_id: '',
    name: '',
    description: '',
    default_monthly_amount: 0,
    variable_cost_type: null,
    default_cost_per_unit: 0
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [categoriesRes, itemsRes] = await Promise.all([
        api.get('/budget/categories'),
        api.get('/budget/items')
      ]);
      
      setCategories(categoriesRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load budget data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Category handlers
  const openCategoryDialog = (category = null) => {
    if (category) {
      setSelectedCategory(category);
      setCategoryForm({
        name: category.name,
        description: category.description || '',
        category_type: category.category_type,
        display_order: category.display_order || 0
      });
    } else {
      setSelectedCategory(null);
      setCategoryForm({
        name: '',
        description: '',
        category_type: 'fixed',
        display_order: categories.length
      });
    }
    setShowCategoryDialog(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryForm.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    try {
      if (selectedCategory) {
        await api.put(`/budget/categories/${selectedCategory.id}`, categoryForm);
        toast.success('Category updated');
      } else {
        await api.post('/budget/categories', categoryForm);
        toast.success('Category created');
      }
      setShowCategoryDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save category'));
    }
  };

  // Item handlers
  const openItemDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setItemForm({
        category_id: item.category_id,
        name: item.name,
        description: item.description || '',
        default_monthly_amount: item.default_monthly_amount || 0,
        variable_cost_type: item.variable_cost_type || null,
        default_cost_per_unit: item.default_cost_per_unit || 0
      });
    } else {
      setSelectedItem(null);
      setItemForm({
        category_id: categories[0]?.id || '',
        name: '',
        description: '',
        default_monthly_amount: 0,
        variable_cost_type: null,
        default_cost_per_unit: 0
      });
    }
    setShowItemDialog(true);
  };

  const handleSaveItem = async () => {
    if (!itemForm.name.trim()) {
      toast.error('Item name is required');
      return;
    }
    if (!itemForm.category_id) {
      toast.error('Please select a category');
      return;
    }
    
    try {
      if (selectedItem) {
        await api.put(`/budget/items/${selectedItem.id}`, itemForm);
        toast.success('Budget item updated');
      } else {
        await api.post('/budget/items', itemForm);
        toast.success('Budget item created');
      }
      setShowItemDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save item'));
    }
  };

  // Delete handlers
  const confirmDelete = (type, item) => {
    setDeleteTarget({ type, item });
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      const { type, item } = deleteTarget;
      if (type === 'category') {
        await api.delete(`/budget/categories/${item.id}`);
        toast.success('Category deleted');
      } else if (type === 'item') {
        await api.delete(`/budget/items/${item.id}`);
        toast.success('Item deleted');
      }
      setShowDeleteDialog(false);
      setDeleteTarget(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete'));
    }
  };

  // Get category by ID
  const getCategoryById = (id) => categories.find(c => c.id === id);
  
  // Get items by category
  const getItemsByCategory = (categoryId) => items.filter(i => i.category_id === categoryId);

  // Get selected category type for item form
  const getSelectedCategoryType = () => {
    const cat = categories.find(c => c.id === itemForm.category_id);
    return cat?.category_type || 'fixed';
  };

  return (
    <div className="space-y-6" data-testid="budget-setup">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/budget')} className="mb-2">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Settings className="h-8 w-8 text-teal-500" />
            Budget Setup
          </h1>
          <p className="text-gray-600 mt-1">
            <Building2 className="h-4 w-4 inline mr-1" />
            Configure budget categories and items at company level
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => navigate('/admin/budget/allocations')} className="bg-teal-600 hover:bg-teal-700">
            <Hotel className="h-4 w-4 mr-2" />
            Hotel Allocations
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-800">
            <strong>Company-Level Setup:</strong> Define budget categories and items here. 
            These will be shared across all hotels in your company. 
            Then allocate specific budgets to each hotel in the "Hotel Allocations" section.
          </p>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="categories">Categories ({categories.length})</TabsTrigger>
            <TabsTrigger value="items">Budget Items ({items.length})</TabsTrigger>
          </TabsList>

          {/* Categories Tab */}
          <TabsContent value="categories">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Budget Categories</CardTitle>
                    <CardDescription>Define fixed and variable cost categories for all hotels</CardDescription>
                  </div>
                  <Button onClick={() => openCategoryDialog()} className="bg-teal-600 hover:bg-teal-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderPlus className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No categories yet. Create your first category.</p>
                    <Button onClick={() => openCategoryDialog()} variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Category
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Items</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {categories.map((category) => (
                        <TableRow key={category.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium">{category.name}</p>
                              {category.description && (
                                <p className="text-sm text-gray-500">{category.description}</p>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={category.category_type === 'fixed' ? 'default' : 'secondary'}>
                              {category.category_type === 'fixed' ? 'Fixed' : 'Variable'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">
                              {getItemsByCategory(category.id).length} items
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant={category.is_active ? 'default' : 'secondary'} className={category.is_active !== false ? 'bg-green-100 text-green-700' : ''}>
                              {category.is_active !== false ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => openCategoryDialog(category)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => confirmDelete('category', category)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Items Tab */}
          <TabsContent value="items">
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Budget Items</CardTitle>
                    <CardDescription>Individual expense items with default amounts (can be overridden per hotel)</CardDescription>
                  </div>
                  <Button 
                    onClick={() => openItemDialog()} 
                    className="bg-teal-600 hover:bg-teal-700"
                    disabled={categories.length === 0}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Item
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {categories.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">Create categories first before adding items.</p>
                    <Button onClick={() => setActiveTab('categories')} variant="outline">
                      Go to Categories
                    </Button>
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-8">
                    <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-4">No budget items yet. Add your first item.</p>
                    <Button onClick={() => openItemDialog()} variant="outline">
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Default Amount/Rate</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {items.map((item) => {
                        const category = getCategoryById(item.category_id);
                        return (
                          <TableRow key={item.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium">{item.name}</p>
                                {item.description && (
                                  <p className="text-sm text-gray-500">{item.description}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">{category?.name || 'Unknown'}</Badge>
                            </TableCell>
                            <TableCell>
                              {category?.category_type === 'fixed' ? (
                                <span className="text-gray-600">Monthly Fixed</span>
                              ) : (
                                <span className="text-amber-600">
                                  {item.variable_cost_type === 'per_room_night' && 'Per Room Night'}
                                  {item.variable_cost_type === 'per_guest_night' && 'Per Guest Night'}
                                  {item.variable_cost_type === 'percentage_revenue' && '% of Revenue'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                              {category?.category_type === 'fixed' 
                                ? formatCurrency(item.default_monthly_amount)
                                : item.variable_cost_type === 'percentage_revenue'
                                  ? `${item.default_cost_per_unit}%`
                                  : formatCurrency(item.default_cost_per_unit)
                              }
                            </TableCell>
                            <TableCell>
                              <Badge variant={item.is_active !== false ? 'default' : 'secondary'} className={item.is_active !== false ? 'bg-green-100 text-green-700' : ''}>
                                {item.is_active !== false ? 'Active' : 'Inactive'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => openItemDialog(item)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => confirmDelete('item', item)}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}

      {/* Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
            <DialogDescription>
              Define a budget category for grouping related expenses
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="cat-name">Category Name *</Label>
              <Input
                id="cat-name"
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Utilities, Salaries, Housekeeping"
              />
            </div>
            <div>
              <Label htmlFor="cat-desc">Description</Label>
              <Textarea
                id="cat-desc"
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Brief description of this category"
                rows={2}
              />
            </div>
            <div>
              <Label>Category Type</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {CATEGORY_TYPES.map(type => (
                  <div
                    key={type.value}
                    className={`p-3 border rounded-lg cursor-pointer transition-all ${
                      categoryForm.category_type === type.value 
                        ? 'border-teal-500 bg-teal-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setCategoryForm({ ...categoryForm, category_type: type.value })}
                  >
                    <p className="font-medium">{type.label}</p>
                    <p className="text-xs text-gray-500">{type.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} className="bg-teal-600 hover:bg-teal-700">
              {selectedCategory ? 'Save Changes' : 'Add Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedItem ? 'Edit Budget Item' : 'Add Budget Item'}</DialogTitle>
            <DialogDescription>
              Define an individual expense item with default amounts
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="item-cat">Category *</Label>
              <Select 
                value={itemForm.category_id} 
                onValueChange={(v) => setItemForm({ ...itemForm, category_id: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name} ({cat.category_type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="item-name">Item Name *</Label>
              <Input
                id="item-name"
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g., Electricity, Water, Staff Salary"
              />
            </div>
            <div>
              <Label htmlFor="item-desc">Description</Label>
              <Textarea
                id="item-desc"
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                placeholder="Optional description"
                rows={2}
              />
            </div>
            
            {getSelectedCategoryType() === 'fixed' ? (
              <div>
                <Label htmlFor="item-amount">Default Monthly Amount (₹)</Label>
                <Input
                  id="item-amount"
                  type="number"
                  value={itemForm.default_monthly_amount}
                  onChange={(e) => setItemForm({ ...itemForm, default_monthly_amount: parseFloat(e.target.value) || 0 })}
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">This default can be overridden for each hotel</p>
              </div>
            ) : (
              <>
                <div>
                  <Label>Variable Cost Type</Label>
                  <div className="grid gap-2 mt-2">
                    {VARIABLE_COST_TYPES.map(type => {
                      const Icon = type.icon;
                      return (
                        <div
                          key={type.value}
                          className={`p-3 border rounded-lg cursor-pointer transition-all flex items-center gap-3 ${
                            itemForm.variable_cost_type === type.value 
                              ? 'border-teal-500 bg-teal-50' 
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setItemForm({ ...itemForm, variable_cost_type: type.value })}
                        >
                          <Icon className="h-5 w-5 text-gray-500" />
                          <div>
                            <p className="font-medium text-sm">{type.label}</p>
                            <p className="text-xs text-gray-500">{type.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <Label htmlFor="item-rate">
                    Default {itemForm.variable_cost_type === 'percentage_revenue' 
                      ? 'Percentage (%)' 
                      : 'Cost Per Unit (₹)'}
                  </Label>
                  <Input
                    id="item-rate"
                    type="number"
                    value={itemForm.default_cost_per_unit}
                    onChange={(e) => setItemForm({ ...itemForm, default_cost_per_unit: parseFloat(e.target.value) || 0 })}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">This default can be overridden for each hotel</p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveItem} className="bg-teal-600 hover:bg-teal-700">
              {selectedItem ? 'Save Changes' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this {deleteTarget?.type}? This action cannot be undone.
              {deleteTarget?.type === 'category' && (
                <p className="mt-2 text-amber-600">
                  Note: Categories with items cannot be deleted. Delete items first.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
